import { cn } from '../../../lib/utils';
import { toast } from '../../ui/sonner';

interface CopyableIdProps {
  /** Full value displayed in the cell and written to the clipboard on click. */
  value: string;
  /** Friendly noun for the toast — e.g. `"ID do programa"`, `"ID da vaga"`. */
  label?: string;
  /** Test id for e2e selectors. */
  'data-testid'?: string;
  className?: string;
}

/**
 * Monospace, click-to-copy cell content for ID-like values (UUIDs, slugs).
 * Truncates with ellipsis when the column is narrower than the value;
 * clicking copies the full value to the clipboard and toasts confirmation.
 */
export function CopyableId({ value, label = 'ID', className, ...rest }: CopyableIdProps) {
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      toast(`${label} copiado`, {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    } catch {
      toast.error('Não foi possível copiar. Tente novamente.');
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      aria-label={`Copiar ${label}`}
      title="Clique para copiar"
      data-testid={rest['data-testid']}
      className={cn(
        'block w-full truncate rounded-sm text-left font-mono text-sm text-muted-foreground transition-colors',
        'hover:text-foreground hover:underline',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className
      )}
    >
      {value}
    </button>
  );
}
